import { Component, computed, effect, inject, Injector, OnInit, signal, ViewChild } from '@angular/core';
import { $state, restManager } from 'app/ajs-upgraded-providers';
import { CliccableActionSales, GridCellFormatterService, GridClickableHistorySalesButtonComponent, GridClientSideComponent, GridFixedButtons, headersTranslate } from 'src/app/shared/components';
import { DataExport, TableData } from 'src/app/shared/model/model';
import { SharedModule } from 'src/app/shared/shared.module';
import { filterableSalesDataSentColumnsWithSearchBar, MassiveSendPreferencesSatus, MassiveSendState, SalesDataSentFe } from '../../history-sales-data-sent.model';
import { subscribeInComponent } from '@tilby/tilby-ui-lib/utilities';
import { ConfigurationManagerService, LoaderService, RIGHT_SIDENAV_PORTAL_DATA_TOKEN, RightSidenavService, ScreenOrientationService, ToolbarEventsService } from 'src/app/core';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { autobind } from 'src/app/models/decorators.model';
import { AlertDialogService, GenericJsonDetailDialogService, GridDownloadDialogService } from 'src/app/dialogs';
import { delay, of } from 'rxjs';
import { RowClickedEvent } from 'ag-grid-community';
import { Sales } from 'tilby-models';
import { mobileCheck } from 'src/utilities';
import { columnsOrder } from 'src/app/shared/components/grid/utils/columns-default/columns-order.model';
import { HistorySaleDetailComponent } from '../../../history-sales/components/history-sale-detail/history-sale-detail.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatChipListbox, MatChipsModule } from '@angular/material/chips';
import { HistoryDataCatalogService } from '../../../services/history-data-catalog.service';
import { groupBy } from 'src/app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { HistoryModuleSections, StatusConfigInterface } from '../../../models/history.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HistoryDataOperationsService } from '../../../services/history-data-operations.service';

@Component({
  selector: 'app-history-sales-data-sent-showcase',
  standalone: true,
  imports: [SharedModule, MatChipsModule],
  templateUrl: './history-sales-data-sent-showcase.component.html',
  styleUrl: './history-sales-data-sent-showcase.component.scss'
})
export class HistorySalesDataSentShowcaseComponent implements OnInit {

  private readonly state = inject($state);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly rightSidenavService = inject(RightSidenavService<HistorySaleDetailComponent>);
  private readonly historyDataCatalogService = inject(HistoryDataCatalogService);
  private readonly gridCellFormatterService = inject(GridCellFormatterService);
  private readonly translateService = inject(TranslateService);
  private readonly restManagerService = inject(restManager);
  private readonly loaderService = inject(LoaderService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManager = inject(ConfigurationManagerService);
  private readonly historyDataOperationsService = inject(HistoryDataOperationsService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  
  protected isMobile = mobileCheck();
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

  @ViewChild('chipList') chipList!: MatChipListbox;
  protected currentStatus = signal('failed');

  private isSelected = signal(false); 

  private allData: any[] = [];
  private groupedData: Record<string, any[]> = {};

  public statusConfig: StatusConfigInterface[] = [
    { status: 'all', 
      label: this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.ALL'), 
      count: 0, selected: false
    },
    { status: 'manage', 
      label: this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.MANAGE'), 
      count: 0, selected: false 
    },
    { status: 'pending', 
      label: this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.PENDING'), 
      count: 0, selected: false 
    },
    { status: 'closed', 
      label: this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.CLOSED'), 
      count: 0, selected: false
    },
    { status: 'failed', 
      label: this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.FAILED'), 
      count: 0, selected: true
    },
  ];
  
  @ViewChild('integrationsGridComponent', {static: true}) gridRef!: GridClientSideComponent;
  protected gridPath = this.state.$current.name.includes('app.new.history.module.sales_data_sent');
  query = {
      pagination: false,
      orderby_asc: "name",
  };
  value = "";
  exportFormatterOptions?: (...any: any[]) => any;
  tableData: TableData[] = [
    {
      rowData$: null,
      dataType: new SalesDataSentFe(),
      headersTranslate: headersTranslate.history_sales_data_sent_showcase,
      columnsOrder: columnsOrder.sales_data_sent,
      columnsFormatter: this.gridCellFormatterService.history_sales_data_sent,
      disabledInternalFilters: ['sale_data_sent.0.status'],
      isRowClickable: false
    }
  ];
  fixedButtons: GridFixedButtons = {
      checkbox: {visible: true, lockPosition: 'left'},
      customButton: {
        visible: true, 
        cellRenderer: GridClickableHistorySalesButtonComponent, 
        lockPosition: 'right',
        cellRendererParams: {
          section: 'sales_data_sent' as HistoryModuleSections
        }
      }
  };

  private movColumnsSwitch = false;
  private isAdvancedFilterActive = false;

  constructor() {
    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_search_bar':
          return this.resetSearchBar();
        case 'toolbar.search':
          return this.gridRef.onFilterTextBoxChanged(event.search, filterableSalesDataSentColumnsWithSearchBar);
        case 'grid.open_tool_panel':
          return this.gridRef.openToolPanel(event.panel);
        default:
          break;
      }
    });
  }

  resetSearchBar() {
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
  }

  ngOnInit(): void {
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
  }

  onGridReady() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      this.updateData();
    })
  }

  calculateCounts(rows: any[], prefStatus: MassiveSendPreferencesSatus) {
    if(!rows) return;

    this.allData = rows;
    this.groupedData = groupBy(rows, (item) =>  this.historyDataOperationsService.getSaleDataSentStatus(item, prefStatus));
    
    this.statusConfig.forEach(status => {
      status.count = status.status === 'all' 
        ? rows.length 
        : this.groupedData[status.status]?.length || 0;
    });
  }

  onFilterStatusChange(newValue: string, prefStatus?: MassiveSendPreferencesSatus) {
    if (!newValue) {
      this.chipList.value = this.currentStatus();
      return;
    }

    this.currentStatus.set(newValue);

    const currentPrefStatus = prefStatus || this.historyDataOperationsService.getMassiveSentPreferences();
    const canShowDeveloperOptions = this.configurationManager.isUserPermittedOptIn("show_developer_options");

    this.gridRef.gridApi?.setGridOption('context', {
      ...this.gridRef.gridApi?.getGridOption('context'),
      readOnlyTable: newValue === 'all',
      massiveSendStatus: currentPrefStatus,
      canShowDeveloperOptions: canShowDeveloperOptions
    });

    this.gridRef.gridApi?.refreshHeader();
    this.gridRef.gridApi?.redrawRows();

    const statusObj = this.statusConfig.find(s => s.status === newValue);

    if(!statusObj) return;

    let filteredRows: any[] = [];
    if(statusObj.status === 'all') {
      filteredRows = this.allData
    } else {
      filteredRows = this.groupedData[statusObj.status] || [];
    }

    this.tableData[0].rowData$ = of(filteredRows).pipe(delay(100));
    this.gridRef.gridApi?.onFilterChanged();
  }

  onFilterChangedEmitter(event: any): void {
    this.createToolbarButtons();
  }

  private getToolbarConfig() {
    const trueSignal = signal(true);
    const selectionActive = this.isSelected(); 
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        name: 'send',
        isIt: () => selectionActive && this.currentStatus() !== 'pending',
        icon: signal('cloud_upload'),
        label: signal('TOPBAR.ACTIONS.MASSIVE_SEND_PMS'),
        click: () => this.massiveSendPms()
      },
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        name: 'columns_panel',
        isIt: trueSignal,
        icon: signal('view_week'),
        label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
        click: () => this.gridRef.openToolPanel('columns')
      },
      {
        isIt: trueSignal,
        name: 'movable_columns',
        icon: signal('drag_indicator'),
        label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
        click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
      },
      {
        isIt: trueSignal,
        name: 'save_columns_position',
        icon: signal('save'),
        label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
        click: () => this.gridRef.openDialogSaveColumnsPreference()
      },
      {
        isIt: () => !this.isMobile,
        name: 'advanced_filter',
        icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
        click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
      },
      {
        isIt: trueSignal,
        name: 'update',
        icon: signal('update'),
        label: signal('ITEMS.TOPBAR_ITEMS.UPDATE'),
        click: () => this.updateData()
      },
      // {
      //   name: 'export',
      //   isIt: () => true,
      //   icon: signal('download'),
      //   label: signal('TOPBAR.ACTIONS.EXPORT'),
      //   click: () => this.triggerExport()
      // },
      {
        isIt: trueSignal,
        name: 'settings_backup_restore',
        icon: signal('settings_backup_restore'),
        label: signal('TOPBAR.ACTIONS.RESET_TABLE'),
        click: () => this.gridRef.resetLayout()
      }
    ];

    return { allButtons, fixedButtons };
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  clearGridFilters() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      const model = this.gridRef.gridApi?.getFilterModel();

      if(model) {
        for (const colId of Object.keys(model)) {
          const filterInstance = await api.getColumnFilterInstance(colId);
          
          if (filterInstance) {
            filterInstance.setModel(null);
          }
        }
      }

      api.onFilterChanged();
    })
  }

  @autobind
  onExport(dataExport: DataExport) {
    const rowSelected = !!this.gridRef.getSelectedCount();
    this.gridRef?.clientSideExport(dataExport, rowSelected);
  }

  async triggerExport() {
    const res = await this.gridDownloadDialogService.openDialog('history_sales_data_sent', {
      data: this.tableData.map(tableData => ({
        ...tableData,
        sortFilterQuery: this.gridRef.getSortFilterQuery(),
        countSelectedRows: this.gridRef.gridApi?.getSelectedRows().length
      }))
    }, "sales");

    if (res) {
      this.onExport(res);
    }
  }

  async updateData() {
    const salesDataSent = await this.historyDataOperationsService.loadSalesDataSentList();
    this.historyDataCatalogService.addDataToCatalog('sales-data-sent', salesDataSent);
    this.updateTable();
  }

  updateTable() {
    const rawData = this.historyDataCatalogService.getDataByKey("sales-data-sent") || [];
    const massiveSendStatus = this.historyDataOperationsService.getMassiveSentPreferences();
    this.calculateCounts(rawData, massiveSendStatus);
    this.onFilterStatusChange(this.currentStatus(), massiveSendStatus);
  }

  rowClicked({data}: RowClickedEvent) {
    // this.movColumnsSwitch && this.gridRef.columnMovableSwitch(this.movColumnsSwitch = false);
    if (this.state.router.globals.transition) {
      return; 
    }

    // this.openDetail(data);
  }


  openDetail(data: Sales|undefined) {
    const portalInjector = Injector.create({
      providers: [{
        provide: RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
        useValue: {sale: data, gridRef: this.gridRef}
      }],
    });
    this.rightSidenavService.componentPortal$.next(new ComponentPortal(HistorySaleDetailComponent, null, portalInjector));
    this.rightSidenavService.isOpen = true;
  }

  actions({ action, data }: CliccableActionSales<Sales>) {
    if(data) {
      switch (action) {
        case "details": 
          return this.openDetail(data);
        case "send-pms":
          return this.sendPms(data);
        case "show-json":
          return this.showJson(data);
      }
    }
  }

  sendPms(data: Sales) {
    if(!data?.id) return;

    const payload = {
      "sale_id": data.id
    }

    this.sendPmsPostApi("tilby_sales/send_pms", payload);
  }

  async showJson(data: Sales) {
    if(!data?.id) return;

    const sale_id = data.id;
    const sale = await this.restManagerService.getOne(`sales_data_sent/${sale_id}`);

    await this.genericJsonDetailDialog.openDialog(
    { data: 
      { title:'HISTORY.SALES_DATA_SENT.DETAILS.TITLE_SHOW_JSON', 
        nameFile: 'sales_data_sent_json_data', 
        json: sale } 
    });
  }

  sendPmsPostApi(url: string, payload: any, isMassive = false) {
    if(!url || !payload) return;

    if(!isMassive) this.loaderService.show();
    this.restManagerService.post(url, payload).then( async (res: any) => {
      if(!isMassive) {
        this._snackBar.open(this.translateService.instant('HISTORY.SALES_DATA_SENT.MESSAGES.SEND_PMS'), undefined, { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        });
      } else {
        const newState: MassiveSendState = {
          updated_at: new Date().toISOString(),
          sale_ids: payload.sale_ids
        };
        await this.configurationManager.setShopPreferenceSync('history.sales_data_sent.massive_send_state', JSON.stringify(newState));
      }
    }).catch((err: any) => {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: 'HISTORY.SALES_DATA_SENT.MESSAGES.SEND_PMS_ERROR'
        }
      });
    }).finally(() => {
      if(!isMassive) this.loaderService.hide();
      this.updateData();
    });
  }

  massiveSendPms() {
    const selectedRows = this.gridRef.gridApi?.getSelectedRows() || [];
    if (selectedRows.length === 0) {
      return;
    }

    const prefState = this.configurationManager.getShopPreference('history.sales_data_sent.massive_send_state');
    const limitSeconds = Number(this.configurationManager.getShopPreference('history.sales_data_sent.massive_send_time_limit') || 0);
    
    if (prefState) {
      const prefStateObj = JSON.parse(prefState);
      const lastUpdate = new Date(prefStateObj.updated_at).getTime();
      const now = Date.now();
      const diffSeconds = (now - lastUpdate) / 1000;

      if (diffSeconds < limitSeconds) {
        const remainingSeconds = Math.ceil(limitSeconds - diffSeconds);
        
        const timeLabel = remainingSeconds > 60 
            ? `${Math.ceil(remainingSeconds / 60)} minuti` 
            : `${remainingSeconds} secondi`;

        const translatedMessage = this.translateService.instant(`HISTORY.SALES_DATA_SENT.MESSAGES.MASSIVE_SEND_LOCKED`, {time: timeLabel});

        this.alertDialogService.openDialog({
          data: {
            messageLabel: translatedMessage,
          }
        });
        return;
      }
    }

    const saleIds = selectedRows.map(row => Number(row.id));
    const payload = { sale_ids: saleIds };

    this.sendPmsPostApi("tilby_sales/send_pms/massive", payload, true);
  }

  handleSelectionChange(selected: boolean) {
    this.isSelected.set(selected);
    this.createToolbarButtons(); 
  }
}